import type { Command, CommandContext } from "./Command";
import { findItem } from "../../inventory/Inventory";
import { getComponent, withComponent } from "../../world/Entity";
import type { EntityId } from "../../world/Entity";
import type { ArtifactComponent, EntityComponent, InspectableComponent } from "../../world/components";

function artifactOf(components: readonly EntityComponent[] | undefined): ArtifactComponent | undefined {
  return components?.find((c): c is ArtifactComponent => c.type === "artifact");
}

const TARNISH_PATTERN = /^A (dull|tarnished|dirty|weathered)/;

/** Rub or clean a tarnished artifact — updates its description and emits knowledge. */
export class RubArtifactCommand implements Command {
  constructor(private readonly entityId: EntityId) {}

  execute(context: CommandContext): void {
    const entity = context.world.getEntity(this.entityId);
    if (entity) {
      const artifactComponent = getComponent(entity, "artifact");
      if (!artifactComponent) throw new Error(`Entity "${this.entityId}" is not an artifact`);
      context.world.updateEntity(this.entityId, (e) =>
        withComponent(e, "inspectable", (c) => ({
          ...c,
          description: c.description.replace(TARNISH_PATTERN, "A gleaming"),
        })),
      );
      context.events.emit("knowledge:unlocked", { nodeId: `cleaned-${artifactComponent.definitionId}`, state: "discovered" });
      return;
    }

    const item = findItem(context.world.inventory, this.entityId);
    if (!item) throw new Error(`Unknown entity "${this.entityId}"`);
    const components = (item.metadata as { components?: readonly EntityComponent[] } | undefined)?.components;
    const artifactComponent = artifactOf(components);
    if (!artifactComponent) throw new Error(`"${this.entityId}" is not an artifact`);

    context.world.updateInventoryItem(this.entityId, (i) => ({
      ...i,
      metadata: {
        ...i.metadata,
        components: (components ?? []).map((c) =>
          c.type === "inspectable"
            ? { ...c, description: (c as InspectableComponent).description.replace(TARNISH_PATTERN, "A gleaming") }
            : c,
        ),
      },
    }));
    context.events.emit("knowledge:unlocked", { nodeId: `cleaned-${artifactComponent.definitionId}`, state: "discovered" });
  }
}
